import schemaJson from '../../../../../shared/projection_line_schema.json';

import { evalFormula } from './formulas';
import {
    FORCED_IS_KEYS,
    FIXED_DRIVERS,
    type CaseInputs,
    type DriverType,
    type Formula,
    type SchemaEntry,
    VALUE_COL_OFFSET,
    type View,
    LOCATION_VIEWS,
} from './types';
import {
    ignoreGroupForLine,
    ignoreGroupForParent,
    isGroupIgnored,
    listEligibleIgnoreGroups,
    type IgnoreGroup,
} from './ignoreGroups';

export type TabData = {
    dates?: unknown[][];
    status?: unknown[][];
    meta?: unknown[][];
    values?: unknown[][];
};

const NO_PICKER_KEYS = new Set([
    'is.gross_product_sales',
    'is.total_orders',
    'is.store_status',
    'bs.cash_bank_accounts_and_petty_cash',
    'bs.less_accumulated_depreciation',
    'bs.less_accumulated_amortization',
    'bs.plus_net_income',
]);

const RESIDUAL_SKIP_KEYS = new Set(['is.total_orders']);

const CARRY_FORWARD_BS_KEYS = new Set(
    (schemaJson as SchemaEntry[])
        .filter((entry) => entry.tab === 'BS' && entry.drivers.includes('carry_forward'))
        .map((entry) => entry.key),
);

export function loadSchema(): SchemaEntry[] {
    return schemaJson as SchemaEntry[];
}

export function indexSchema(schema: SchemaEntry[]): {
    byKey: Record<string, SchemaEntry>;
    isEntries: SchemaEntry[];
    bsEntries: SchemaEntry[];
    formulaEntries: SchemaEntry[];
} {
    const byKey: Record<string, SchemaEntry> = {};
    const isEntries: SchemaEntry[] = [];
    const bsEntries: SchemaEntry[] = [];
    const formulaEntries: SchemaEntry[] = [];

    for (const entry of schema) {
        byKey[entry.key] = entry;
        if (entry.tab === 'IS') {
            isEntries.push(entry);
        } else {
            bsEntries.push(entry);
        }
        if (
            entry.formula &&
            (entry.kind === 'subtotal' || entry.kind === 'metric') &&
            entry.row != null
        ) {
            formulaEntries.push(entry);
        }
    }

    formulaEntries.sort((a, b) => (a.row ?? 0) - (b.row ?? 0));
    return { byKey, isEntries, bsEntries, formulaEntries };
}

function basePickableDrivers(entry: SchemaEntry): DriverType[] {
    return entry.drivers.filter(
        (driver): driver is DriverType =>
            !FIXED_DRIVERS.includes(driver as DriverType),
    );
}

function parentDriversWhenIgnored(
    schema: SchemaEntry[],
    group: IgnoreGroup,
    view: View,
    consolidatedOnly: string[],
): DriverType[] {
    const union = new Set<DriverType>();
    for (const member of group.members) {
        for (const driver of allowedDrivers(schema, member, view, consolidatedOnly, undefined, {
            includeIgnore: false,
        })) {
            if (driver !== 'ignore' && driver !== 'carry_forward') {
                union.add(driver);
            }
        }
    }
    const pickable = [...union];
    if (pickable.length === 0) {
        return [];
    }
    return ['carry_forward', ...pickable];
}

export function allowedDrivers(
    schema: SchemaEntry[],
    lineKey: string,
    view: View,
    consolidatedOnly: string[] = [],
    inputs?: CaseInputs,
    options: { includeIgnore?: boolean } = {},
): DriverType[] {
    const includeIgnore = options.includeIgnore !== false;
    const entry = schema.find((row) => row.key === lineKey);
    if (!entry) {
        return [];
    }
    if (entry.bs_only && view !== 'consolidated') {
        return [];
    }
    if (view !== 'consolidated' && consolidatedOnly.includes(lineKey)) {
        return [];
    }
    if (
        view === 'consolidated' &&
        entry.tab === 'IS' &&
        !consolidatedOnly.includes(lineKey) &&
        !entry.bs_only
    ) {
        return [];
    }
    if (NO_PICKER_KEYS.has(lineKey) || entry.kind === 'text' || entry.computed) {
        return [];
    }
    if (CARRY_FORWARD_BS_KEYS.has(lineKey)) {
        return ['carry_forward'];
    }

    const memberGroup = ignoreGroupForLine(schema, lineKey);
    if (memberGroup && memberGroup.members.includes(lineKey) && entry.kind === 'input') {
        const pickable = basePickableDrivers(entry);
        if (pickable.length === 0) {
            return [];
        }
        return includeIgnore
            ? ['carry_forward', 'ignore', ...pickable]
            : ['carry_forward', ...pickable];
    }

    const parentGroup = ignoreGroupForParent(schema, lineKey);
    if (
        parentGroup &&
        parentGroup.parent === lineKey &&
        inputs &&
        isGroupIgnored(inputs, view, parentGroup)
    ) {
        return parentDriversWhenIgnored(schema, parentGroup, view, consolidatedOnly);
    }

    if (entry.kind === 'subtotal') {
        return [];
    }

    let pickable = basePickableDrivers(entry);
    if (lineKey === 'is.payroll_wages') {
        pickable = pickable.filter(
            (driver) =>
                driver !== 'labor_buildup' ||
                (view !== 'consolidated' && LOCATION_VIEWS.includes(view as (typeof LOCATION_VIEWS)[number])),
        );
    } else {
        pickable = pickable.filter((driver) => driver !== 'labor_buildup');
    }
    if (pickable.length === 0) {
        return [];
    }
    return ['carry_forward', ...pickable];
}

export function eligibleIgnoreGroupCount(schema: SchemaEntry[]): number {
    return listEligibleIgnoreGroups(schema).length;
}

export function parseNumeric(
    value: unknown,
    options: { textMode?: boolean } = {},
): number | string | null {
    if (options.textMode) {
        if (value == null || value === '') {
            return null;
        }
        return String(value).trim();
    }
    if (value == null) {
        return null;
    }
    if (typeof value === 'boolean') {
        return null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    const text = String(value).trim();
    if (!text || ['-', 'na', 'n/a', '#n/a'].includes(text.toLowerCase())) {
        return null;
    }
    if (text.startsWith('(') && text.endsWith(')')) {
        const inner = text.slice(1, -1).replace(/,/g, '').replace(/%/g, '').trim();
        const parsed = Number(inner);
        return Number.isFinite(parsed) ? -parsed : null;
    }
    const normalized = text.replace(/,/g, '').replace(/%/g, '').trim();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
}

export function actualMonthIndices(statusRow: unknown[][]): number[] {
    if (!statusRow.length) {
        return [];
    }
    const statuses = statusRow[0] as unknown[];
    const out: number[] = [];
    for (let i = 0; i < statuses.length; i += 1) {
        if (i < VALUE_COL_OFFSET) {
            continue;
        }
        if (String(statuses[i] ?? '').trim().toLowerCase() === 'actual') {
            out.push(i - VALUE_COL_OFFSET);
        }
    }
    return out;
}

export function rowValueForMonth(
    values: unknown[][],
    schemaRow: number,
    monthIdx: number,
): number | null {
    const offset = schemaRow - 21;
    if (offset < 0 || offset >= values.length) {
        return null;
    }
    const row = values[offset] as unknown[];
    if (monthIdx >= row.length) {
        return null;
    }
    const parsed = parseNumeric(row[monthIdx]);
    return typeof parsed === 'number' ? parsed : null;
}

export function toleranceFor(entry: SchemaEntry): number {
    if (entry.unit === 'count') {
        return 0.05;
    }
    if (entry.unit === 'pct') {
        return 0.0005;
    }
    return 0.005;
}

export function recomputeFormulas(
    schema: SchemaEntry[],
    valuesByKey: Record<string, number | null>,
    tabFilter: 'IS' | 'BS',
    options: { zeroOrders?: boolean } = {},
): Record<string, number | null> {
    const formulaEntries = schema.filter(
        (entry) =>
            entry.tab === tabFilter &&
            entry.formula &&
            (entry.kind === 'subtotal' || entry.kind === 'metric') &&
            entry.row != null,
    );
    formulaEntries.sort((a, b) => (a.row ?? 0) - (b.row ?? 0));

    const out = { ...valuesByKey };
    for (let pass = 0; pass < formulaEntries.length + 2; pass += 1) {
        for (const entry of formulaEntries) {
            if (RESIDUAL_SKIP_KEYS.has(entry.key)) {
                continue;
            }
            if (
                options.zeroOrders &&
                FORCED_IS_KEYS.includes(entry.key as (typeof FORCED_IS_KEYS)[number])
            ) {
                continue;
            }
            out[entry.key] = evalFormula(entry.formula as Formula, out);
        }
    }
    return out;
}

export interface ResidualOffender {
    tab: string;
    row: number;
    key: string;
    month: string;
    computed: number | null;
    imported: number | null;
    residual: number;
}

export function residualCheckTab(
    tabName: string,
    tabData: TabData,
    schema: SchemaEntry[],
    tabFilter: 'IS' | 'BS',
): { offenders: ResidualOffender[]; nActual: number } {
    const offenders: ResidualOffender[] = [];
    const status = tabData.status ?? [];
    const values = tabData.values ?? [];
    const dates = tabData.dates ?? [[]];
    const dateCells = (dates[0] ?? []) as unknown[];
    const months = actualMonthIndices(status);
    const formulaEntries = schema.filter(
        (entry) =>
            entry.tab === tabFilter &&
            entry.formula &&
            (entry.kind === 'subtotal' || entry.kind === 'metric') &&
            entry.row != null,
    );
    formulaEntries.sort((a, b) => (a.row ?? 0) - (b.row ?? 0));

    for (const monthIdx of months) {
        const valuesByKey: Record<string, number | null> = {};
        for (const entry of schema) {
            if (entry.tab !== tabFilter || entry.row == null) {
                continue;
            }
            if (entry.kind === 'text') {
                continue;
            }
            if (entry.kind === 'input') {
                valuesByKey[entry.key] = rowValueForMonth(values, entry.row, monthIdx);
            } else if (entry.kind === 'subtotal' && !entry.formula) {
                valuesByKey[entry.key] = rowValueForMonth(values, entry.row, monthIdx);
            } else if (entry.kind === 'subtotal' && entry.formula && RESIDUAL_SKIP_KEYS.has(entry.key)) {
                valuesByKey[entry.key] = rowValueForMonth(values, entry.row, monthIdx);
            }
        }

        const totalOrders = valuesByKey['is.total_orders'];
        const zeroOrders =
            tabFilter === 'IS' && (totalOrders == null || totalOrders === 0);
        if (zeroOrders) {
            const schemaByKey = Object.fromEntries(schema.map((e) => [e.key, e]));
            for (const key of FORCED_IS_KEYS) {
                const row = schemaByKey[key]?.row;
                if (row == null) {
                    continue;
                }
                valuesByKey[key] = rowValueForMonth(values, row, monthIdx);
            }
        }

        const computed = recomputeFormulas(schema, valuesByKey, tabFilter, { zeroOrders });

        for (const entry of formulaEntries) {
            if (RESIDUAL_SKIP_KEYS.has(entry.key)) {
                continue;
            }
            const computedValue = computed[entry.key] ?? null;
            const imported = rowValueForMonth(values, entry.row as number, monthIdx);
            if (computedValue == null && imported == null) {
                continue;
            }
            if (computedValue == null && imported === 0 && entry.unit === 'count') {
                continue;
            }
            const tol = toleranceFor(entry);
            let residual: number | null = null;
            if (computedValue != null && imported != null) {
                if (entry.unit === 'pct' && Math.abs(imported) > 1.5) {
                    residual = Math.abs(computedValue * 100 - imported);
                } else {
                    residual = Math.abs(computedValue - imported);
                }
            } else if (computedValue !== imported) {
                residual = 999;
            }
            if (residual != null && residual > tol) {
                const dateIdx = monthIdx + VALUE_COL_OFFSET;
                const dateLabel =
                    dateIdx < dateCells.length ? String(dateCells[dateIdx] ?? '') : '';
                offenders.push({
                    tab: tabName,
                    row: entry.row as number,
                    key: entry.key,
                    month: dateLabel,
                    computed: computedValue,
                    imported,
                    residual,
                });
            }
        }
    }

    return { offenders, nActual: months.length };
}

export function bsBalanceResiduals(
    tabData: TabData,
    schema: SchemaEntry[] = loadSchema(),
): { month: string; assets: number; le: number; diff: number }[] {
    const assetsRow = schema.find((e) => e.key === 'bs.total_assets')?.row;
    const leRow = schema.find((e) => e.key === 'bs.total_liabilities_and_equity')?.row;
    if (assetsRow == null || leRow == null) {
        return [];
    }
    const offenders: { month: string; assets: number; le: number; diff: number }[] = [];
    const status = tabData.status ?? [];
    const values = tabData.values ?? [];
    const dates = tabData.dates ?? [[]];
    const dateCells = (dates[0] ?? []) as unknown[];
    const months = actualMonthIndices(status);

    for (const monthIdx of months) {
        const assets = rowValueForMonth(values, assetsRow, monthIdx);
        const le = rowValueForMonth(values, leRow, monthIdx);
        if (assets == null || le == null) {
            continue;
        }
        const diff = assets - le;
        if (Math.abs(diff) > 0.005) {
            const dateIdx = monthIdx + VALUE_COL_OFFSET;
            const dateLabel =
                dateIdx < dateCells.length ? String(dateCells[dateIdx] ?? '') : '';
            offenders.push({ month: dateLabel, assets, le, diff });
        }
    }
    return offenders;
}
